import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  Avatar,
  Badge,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsOutlined from '@mui/icons-material/NotificationsOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  notificationService,
  AppNotification,
} from '../../services/notificationService';

const POLL_INTERVAL_MS = 30_000;

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function fullTimestamp(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

/** Visual identity (icon + color) derived from the notification type. */
function visualForType(type: string): { icon: React.ReactNode; bg: string; color: string } {
  const t = (type || '').toUpperCase();
  if (t.includes('ORDER')) {
    return { icon: <ShoppingCartCheckoutIcon sx={{ fontSize: 18 }} />, bg: '#ECFDF5', color: '#059669' };
  }
  if (t.includes('PLACEMENT')) {
    return { icon: <ArticleOutlinedIcon sx={{ fontSize: 18 }} />, bg: '#EFF6FF', color: '#2563EB' };
  }
  if (t.includes('LINK')) {
    return { icon: <LinkOffIcon sx={{ fontSize: 18 }} />, bg: '#FEF2F2', color: '#DC2626' };
  }
  return { icon: <CampaignOutlinedIcon sx={{ fontSize: 18 }} />, bg: '#F5F3FF', color: '#7C3AED' };
}

const NotificationBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'unread' | 'read'>('unread');
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Lightweight poll: just the count, every 30s. Full list is fetched lazily
  // when the drawer opens.
  const { data: countData } = useQuery({
    queryKey: ['notification-count'],
    queryFn: () => notificationService.count(),
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: POLL_INTERVAL_MS,
  });
  const unreadCount = countData?.data?.unreadCount ?? 0;

  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['notification-list'],
    queryFn: () => notificationService.list({ limit: 50 }),
    enabled: open,
    staleTime: 10_000,
  });
  const notifications: AppNotification[] = useMemo(
    () => listData?.data?.notifications ?? [],
    [listData],
  );

  const unreadList = useMemo(() => notifications.filter((n) => !n.isRead), [notifications]);
  const readList = useMemo(() => notifications.filter((n) => n.isRead), [notifications]);
  const visibleList = tab === 'unread' ? unreadList : readList;

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationService.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-count'] });
      queryClient.invalidateQueries({ queryKey: ['notification-list'] });
    },
  });

  const markAllMutation = useMutation({
    mutationFn: () => notificationService.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-count'] });
      queryClient.invalidateQueries({ queryKey: ['notification-list'] });
    },
  });

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => {
    setOpen(false);
    // MUI scroll-lock can stick after closing + navigate; clear it.
    window.setTimeout(() => {
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
    }, 0);
  }, []);

  const handleNotificationClick = (n: AppNotification) => {
    if (!n.isRead) markReadMutation.mutate(n.id);
    if (!n.link) return;
    handleClose();

    // Always open global Orders menu. Prefer exact order/placement ids from the
    // notification entity so we can fetch + scroll to the same order card.
    let target = n.link;
    if (target.startsWith('/guest-blog-placements')) {
      const qs = new URLSearchParams();
      try {
        const parsed = new URL(target, window.location.origin);
        parsed.searchParams.forEach((v, k) => qs.set(k, v));
      } catch {
        /* ignore */
      }
      qs.set('standalone', '1');
      // Unique per click so same-page notification clicks always re-trigger scroll
      qs.set('nf', `${n.id}_${Date.now()}`);

      const et = (n.entityType || '').toUpperCase();
      if (et === 'PAYMENT_ATTEMPT' && n.entityId) {
        qs.set('paymentAttemptId', n.entityId);
        if (!qs.get('status')) qs.set('status', 'READY_TO_PUBLISH');
      } else if ((et === 'PLACEMENT' || et === 'placement') && n.entityId) {
        qs.set('placementId', n.entityId);
      } else if ((et === 'PROJECT' || et === 'project') && n.entityId) {
        if (!qs.get('projectId')) qs.set('projectId', n.entityId);
        qs.set('focus', '1');
        if (!qs.get('status')) qs.set('status', 'READY_TO_PUBLISH');
      } else if (!qs.get('paymentAttemptId') && !qs.get('placementId') && qs.get('projectId')) {
        qs.set('focus', '1');
      }

      // Legacy PROJECT notifications share one projectId — disambiguate by
      // site count + order total parsed from the notification message.
      if (!qs.get('paymentAttemptId') && !qs.get('placementId') && n.message) {
        const sitesMatch = n.message.match(/(\d+)\s+sites?/i);
        const totalMatch = n.message.match(/\$([0-9]+(?:\.[0-9]+)?)/);
        if (sitesMatch) qs.set('focusSites', sitesMatch[1]);
        if (totalMatch) qs.set('focusTotal', totalMatch[1]);
      }

      target = `/guest-blog-placements?${qs.toString()}`;
    }

    // Delay so the drawer can close; force navigation even when already on Orders.
    window.setTimeout(() => {
      navigate(target);
    }, 50);
  };

  const handleMarkOne = (e: React.MouseEvent, n: AppNotification) => {
    e.stopPropagation();
    if (!n.isRead) markReadMutation.mutate(n.id);
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          ref={buttonRef}
          onClick={handleOpen}
          sx={{
            color: 'var(--text-muted)',
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: 'var(--bg)',
              color: 'var(--text-primary)',
            },
          }}
        >
          <Badge
            color="error"
            badgeContent={unreadCount}
            max={99}
            overlap="circular"
            sx={{ '& .MuiBadge-badge': { fontSize: 11, height: 18, minWidth: 18 } }}
          >
            <NotificationsOutlined sx={{ fontSize: 20, width: '1.3em', height: '1.3em' }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        disableScrollLock
        ModalProps={{ keepMounted: false }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--surface, #fff)',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 2,
            borderBottom: '1px solid var(--border-light, #eef0f4)',
          }}
        >
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>
            Notifications
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Mark all as read">
              <span>
                <IconButton
                  size="small"
                  onClick={() => markAllMutation.mutate()}
                  disabled={markAllMutation.isPending || unreadCount === 0}
                  sx={{ color: 'var(--text-muted)' }}
                >
                  <DoneAllIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton size="small" onClick={handleClose} sx={{ color: 'var(--text-muted)' }}>
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 2,
            minHeight: 44,
            borderBottom: '1px solid var(--border-light, #eef0f4)',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: 14,
              fontWeight: 600,
              minHeight: 44,
              color: 'var(--text-muted)',
            },
            '& .Mui-selected': { color: 'var(--accent, #2563EB) !important' },
            '& .MuiTabs-indicator': { backgroundColor: 'var(--accent, #2563EB)' },
          }}
        >
          <Tab
            value="unread"
            label={unreadList.length ? `Unread (${unreadList.length})` : 'Unread'}
          />
          <Tab value="read" label="Read" />
        </Tabs>

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {listLoading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : visibleList.length === 0 ? (
            <Box sx={{ py: 8, px: 3, textAlign: 'center' }}>
              <NotificationsOutlined sx={{ fontSize: 40, color: 'var(--border)', mb: 1 }} />
              <Typography sx={{ fontSize: 14, color: 'var(--text-muted)' }}>
                {tab === 'unread' ? 'You are all caught up' : 'No read notifications'}
              </Typography>
            </Box>
          ) : (
            visibleList.map((n) => {
              const v = visualForType(n.type);
              const tooltipBody = (
                <Box sx={{ maxWidth: 320 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, mb: n.message ? 0.5 : 0 }}>
                    {n.subject}
                  </Typography>
                  {n.message && (
                    <Typography sx={{ fontSize: 12, whiteSpace: 'pre-wrap', mb: 0.5 }}>
                      {n.message}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: 11, opacity: 0.8 }}>
                    {fullTimestamp(n.createdAt)}
                  </Typography>
                </Box>
              );

              return (
                <Tooltip key={n.id} title={tooltipBody} placement="left" arrow enterDelay={400}>
                  <Box
                    onClick={() => handleNotificationClick(n)}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      gap: 1.5,
                      px: 2.5,
                      py: 1.75,
                      cursor: n.link ? 'pointer' : 'default',
                      borderBottom: '1px solid var(--border-light, #f1f3f7)',
                      background: n.isRead ? 'transparent' : 'rgba(37, 99, 235, 0.045)',
                      transition: 'background 0.15s',
                      '&:hover': { background: 'var(--bg, #f8fafc)' },
                      '&:hover .notif-mark-btn': { opacity: 1 },
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      sx={{ width: 36, height: 36, bgcolor: v.bg, color: v.color, flexShrink: 0 }}
                    >
                      {v.icon}
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 13.5,
                          fontWeight: n.isRead ? 500 : 700,
                          color: 'var(--text-primary)',
                          lineHeight: 1.35,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {n.subject}
                      </Typography>
                      {n.message && (
                        <Typography
                          sx={{
                            fontSize: 12.5,
                            color: 'var(--text-secondary)',
                            mt: 0.25,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {n.message}
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: 11, color: 'var(--text-muted)', mt: 0.5 }}>
                        {timeAgo(n.createdAt)}
                      </Typography>
                    </Box>

                    {/* Per-notification read control */}
                    {!n.isRead ? (
                      <Tooltip title="Mark as read">
                        <IconButton
                          className="notif-mark-btn"
                          size="small"
                          onClick={(e) => handleMarkOne(e, n)}
                          sx={{
                            alignSelf: 'center',
                            opacity: { xs: 1, sm: 0 },
                            transition: 'opacity 0.15s',
                            color: 'var(--accent, #2563EB)',
                          }}
                        >
                          <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <CheckCircleOutlineIcon
                        sx={{ alignSelf: 'center', fontSize: 18, color: 'var(--border)' }}
                      />
                    )}

                    {!n.isRead && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 3,
                          background: 'var(--accent, #2563EB)',
                        }}
                      />
                    )}
                  </Box>
                </Tooltip>
              );
            })
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default NotificationBell;
